import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner';
import { Button, Container, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import PropertySelectComponent from './extra/PropertySelectComponent';
import RoomSelectComponent from './extra/RoomSelectComponent';
import DateBaseComponent from './extra/DateBaseComponent';
import DateRangePickerComponent from './extra/DateRangePickerComponent';
import { FilterSchema, FilterSchemaType } from '../../../services/extra-report/schema/FilterSchema';
import useExtraReport from '../../../services/extra-report/hooks/useExtraReport';
import ExtraTableReport from './extra/ExtraTableReport';
import SkeletonTable from './breakfast/SkeletonTable';

const ExtraReportComponent: React.FC = () => {
    const { control, handleSubmit, formState: { errors }, setValue, watch } = useForm<FilterSchemaType>({
        resolver: zodResolver(FilterSchema),
        defaultValues: {
            filter: {
                property_id: "",
                start_date: "",
                end_date: "",
                date_base: "extra_date",
                room_type_id: "",
            },
        },
    });

    const queryClient = useQueryClient();

    // State untuk melacak parameter filter terakhir yang di-submit
    const [appliedParams, setAppliedParams] = useState<{
        property_id: string;
        start_date: string;
        end_date: string;
        date_base: string;
        room_type_id: string;
    } | null>(null);

    // State untuk menyimpan filter yang akan digunakan saat fetching
    const [filter, setFilter] = useState<FilterSchemaType["filter"]>({
        property_id: "",
        start_date: "",
        end_date: "",
        date_base: "extra_date",
        room_type_id: "",
    });

    const { data: extraReportData, isFetching: isFetchingExtraReport } = useExtraReport({
        filter: filter,
    });

    // Deteksi apakah form memiliki perubahan dibandingkan dengan hasil submit terakhir
    const currentFormValues = watch();
    const isFormChanged = appliedParams
        ? currentFormValues.filter?.property_id !== appliedParams.property_id ||
          currentFormValues.filter?.start_date !== appliedParams.start_date ||
          currentFormValues.filter?.end_date !== appliedParams.end_date ||
          currentFormValues.filter?.date_base !== appliedParams.date_base ||
          (currentFormValues.filter?.room_type_id || "") !== appliedParams.room_type_id
        : false;

    const showRefresh = !isFetchingExtraReport && extraReportData && appliedParams !== null && !isFormChanged;

    // Fungsi tunggal untuk menangani submit
    const onSubmit = useCallback(
        (data: FilterSchemaType) => {
            console.log('data', data);

            const updatedFilter = { ...data.filter };

            if (showRefresh) {
                queryClient.invalidateQueries({
                    queryKey: ['extra-report']
                });
            }

            setAppliedParams({
                property_id: data.filter.property_id,
                start_date: data.filter.start_date,
                end_date: data.filter.end_date,
                date_base: data.filter.date_base || "extra_date",
                room_type_id: data.filter.room_type_id || "",
            });

            setFilter(updatedFilter);
        },
        [setFilter, showRefresh, queryClient]
    );

    // Toast notifikasi saat data berhasil di-load
    useEffect(() => {
        if (!isFetchingExtraReport && extraReportData) {
            toast.success('Extra report data loaded successfully.');
        }
    }, [isFetchingExtraReport, extraReportData]);

    return (
        <Container fluid className="dashboard-content">
            <form onSubmit={handleSubmit((data) => onSubmit(data))}>
                <div className="d-flex flex-column flex-md-row justify-content-between gap-4">
                    {/* Kiri: Filter Components */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="w-100">
                            <PropertySelectComponent setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <RoomSelectComponent control={control} errors={errors} setValue={setValue} />
                        </div>

                        <div className="w-100">
                            <DateBaseComponent control={control} errors={errors} setValue={setValue} />
                        </div>
                    </div>

                    {/* Kanan: Date Range & Submit */}
                    <div className="d-flex flex-column flex-md-row gap-3 w-100">
                        <div className="d-flex flex-column gap-2 w-100">
                            <DateRangePickerComponent control={control} errors={errors} setValue={setValue} />
                            <Button variant={showRefresh ? "info" : "warning"} type="submit" disabled={isFetchingExtraReport}>
                                {isFetchingExtraReport ? (
                                    <><Spinner animation="border" size="sm" className="me-2" />Loading...</>
                                ) : showRefresh ? (
                                    <><i className="bi bi-arrow-clockwise me-2"></i>Refresh Extra Report</>
                                ) : (
                                    'Show Extra Report'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </form>
            {isFetchingExtraReport && <SkeletonTable columns={8} rows={10} />}
            {!isFetchingExtraReport && extraReportData && (
                <>
                    <ExtraTableReport data={extraReportData} />
                </>
            )}
        </Container>
    )
}

export default ExtraReportComponent
